import { toDateStandardFormat } from '@/helpers';
import { TUseIntl } from '@/hooks/useIntl';
import { EndingPolicy } from '@/pages/PolicyRenewals';
import { Result } from '@/types/base';
import { AgGridRequest } from '@/types/models';
import { ClockCircleOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { Modal } from 'antd';
import moment from 'moment';
import request from './request';

export interface EndingPoliciesDetailsRequest extends AgGridRequest {
    productId?: string;
    agencyId?: string;
    fromEndDateUtc?: string | null;
    toEndDateUtc?: string | null;
}

export interface EndingPolicySector {
    sectorType: string;
    count: number;
}

export interface EndingPoliciesCheck {
    fromUtc: string;
    toUtc: string;
    sectors: EndingPolicySector[];
    totalCount: number;
}

interface EndingPoliciesCache extends EndingPoliciesCheck {
    date: string;
    popupShown?: boolean;
}

class EndingPoliciesNotification {
    private storageKey = 'ending-policies-check';

    public pageUrl = '/policy/renewals';

    public refreshEvent = 'ending-policies-refresh';

    public pageChangeEvent = 'ending-policies-page-change';

    private inFlight = false;

    private refreshTimer: number | null = null;

    private popupDisplayedToday = false;

    public async getEndingPoliciesDetails(params: EndingPoliciesDetailsRequest): Promise<Result<EndingPolicy[]>> {
        return request('/policy/get-ending-policies-details', {
            method: 'POST',
            data: params,
        });
    }

    public async checkEndingPolicies(): Promise<
        Result<{
            fromUtc: string;
            toUtc: string;
            sectors: {
                sectorType: string;
                count: number;
            }[];
        }>
    > {
        return request('/policy/check-ending-policies', {
            method: 'GET',
        });
    }

    private getTodayKey() {
        return moment().format('YYYY-MM-DD');
    }

    private getTotalCount(sectors: EndingPolicySector[] = []) {
        return sectors.reduce((sum, sector) => sum + (sector.count || 0), 0);
    }

    public getCache(): EndingPoliciesCache | null {
        try {
            const raw = localStorage.getItem(this.storageKey);
            if (!raw) return null;

            return JSON.parse(raw) as EndingPoliciesCache;
        } catch {
            return null;
        }
    }

    public getCachedCount(): number {
        const cache = this.getCache();
        if (!cache || cache.date !== this.getTodayKey()) return 0;

        return cache.totalCount || 0;
    }

    public clearCache() {
        this.popupDisplayedToday = false;
        localStorage.removeItem(this.storageKey);
    }

    public invalidateCache() {
        this.clearCache();

        if (this.refreshTimer) {
            window.clearTimeout(this.refreshTimer);
        }

        this.refreshTimer = window.setTimeout(() => {
            window.dispatchEvent(new Event(this.refreshEvent));
        }, 400);
    }

    private saveCache(data: EndingPoliciesCheck) {
        const payload: EndingPoliciesCache = {
            ...data,
            date: this.getTodayKey(),
        };

        localStorage.setItem(this.storageKey, JSON.stringify(payload));
    }

    public async fetchIfNeeded(): Promise<{ data: EndingPoliciesCheck; isFresh: boolean; popupShown: boolean } | null> {
        const cache = this.getCache();

        if (cache?.date === this.getTodayKey()) {
            if (cache.popupShown) this.popupDisplayedToday = true;

            return {
                data: {
                    fromUtc: cache.fromUtc,
                    toUtc: cache.toUtc,
                    sectors: cache.sectors || [],
                    totalCount: cache.totalCount || this.getTotalCount(cache.sectors),
                },
                isFresh: false,
                popupShown: !!cache.popupShown,
            };
        }

        this.popupDisplayedToday = false;

        if (this.inFlight) return null;

        this.inFlight = true;

        try {
            const response = await this.checkEndingPolicies();

            if (response.data === null) return null;

            const data: EndingPoliciesCheck = {
                fromUtc: response.data.fromUtc,
                toUtc: response.data.toUtc,
                sectors: response.data.sectors || [],
                totalCount: this.getTotalCount(response.data.sectors),
            };

            this.saveCache(data);

            return { data, isFresh: true, popupShown: false };
        } finally {
            this.inFlight = false;
        }
    }

    public markPopupShown() {
        this.popupDisplayedToday = true;

        const cache = this.getCache();

        if (!cache || cache.date !== this.getTodayKey()) return;

        localStorage.setItem(
            this.storageKey,
            JSON.stringify({
                ...cache,
                popupShown: true,
            })
        );
    }

    public shouldShowPopup(data: EndingPoliciesCheck, popupShown: boolean) {
        if (this.popupDisplayedToday || popupShown) return false;
        if (!data.totalCount) return false;
        if (history.location.pathname === this.pageUrl) return false;

        return true;
    }

    public showPopup(data: EndingPoliciesCheck, t: TUseIntl) {
        if (!this.shouldShowPopup(data, false)) return;

        this.markPopupShown();

        Modal.confirm({
            title: (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <ClockCircleOutlined style={{ color: '#005CA9', fontSize: 20 }} />
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#1f1f1f', lineHeight: '22px' }}>{t('menu.policy-renewals')}</div>
                    </div>
                    <div style={{ marginTop: 2, fontSize: 14, fontWeight: 400, color: '#8c8c8c' }}>
                        {toDateStandardFormat(data.fromUtc)} – {toDateStandardFormat(data.toUtc)}
                    </div>
                </div>
            ),
            width: 400,
            icon: null,
            style: {
                marginTop: -200,
            },
            okText: t('endingPolicies.view'),
            cancelText: t('common.close'),
            centered: true,
            okButtonProps: { type: 'primary' },
            onOk: () => history.push(this.pageUrl),
            content: (
                <div style={{ marginTop: 8 }}>
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                        {(data.sectors || []).map((sector, index) => (
                            <div
                                key={sector.sectorType}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 12px',
                                    fontSize: 13,
                                    borderTop: index === 0 ? '' : '1px solid #f0f0f0',
                                    background: '#fff',
                                }}
                            >
                                <span style={{ color: '#595959' }}>{t(`menu.${String(sector.sectorType || '').toLowerCase()}`)}</span>
                                <span style={{ fontWeight: 600, color: '#005CA9', fontVariantNumeric: 'tabular-nums' }}>{sector.count}</span>
                            </div>
                        ))}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px 12px',
                                background: '#f5f8fb',
                                borderTop: '1px solid #e6edf5',
                                fontSize: 13,
                            }}
                        >
                            <span style={{ fontWeight: 600 }}>{t('endingPolicies.totalLabel')}</span>
                            <span style={{ fontWeight: 700, color: '#005CA9', fontVariantNumeric: 'tabular-nums' }}>{data.totalCount}</span>
                        </div>
                    </div>
                </div>
            ),
        });
    }
}

export default new EndingPoliciesNotification();
